import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Zap, Trophy, Swords, BarChart3, ShoppingBag, ArrowLeft, Timer, Target,
  Flame, Star, Crown, Shield, ChevronRight, RotateCcw, Users, Loader2, Check, X
} from "lucide-react";

// ===================== SOUND EFFECTS (Web Audio API) =====================
const audioCtx = typeof window !== "undefined" ? new (window.AudioContext || window.webkitAudioContext)() : null;

function playTone(freq, duration, type = "sine", gain = 0.3) {
  if (!audioCtx) return;
  if (audioCtx.state === "suspended") audioCtx.resume();
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(gain, audioCtx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);
  osc.connect(g).connect(audioCtx.destination);
  osc.start();
  osc.stop(audioCtx.currentTime + duration);
}

function playCorrect() {
  playTone(880, 0.12, "sine", 0.25);
  setTimeout(() => playTone(1320, 0.15, "sine", 0.2), 80);
}

function playWrong() {
  playTone(200, 0.15, "square", 0.15);
  setTimeout(() => playTone(160, 0.2, "square", 0.12), 100);
}

function playStreak() {
  playTone(660, 0.08, "sine", 0.2);
  setTimeout(() => playTone(880, 0.08, "sine", 0.2), 70);
  setTimeout(() => playTone(1100, 0.08, "sine", 0.2), 140);
  setTimeout(() => playTone(1320, 0.12, "sine", 0.25), 210);
}

// ===================== ANIMATION STYLES =====================
const flashKeyframes = `
@keyframes arena-correct { 0% { transform: scale(1); background-color: transparent; } 30% { transform: scale(1.08); background-color: rgba(34,197,94,0.15); } 100% { transform: scale(1); background-color: transparent; } }
@keyframes arena-wrong { 0%,100% { transform: translateX(0); } 15%,45%,75% { transform: translateX(-6px); } 30%,60% { transform: translateX(6px); } }
@keyframes arena-streak-fire { 0% { transform: scale(0) rotate(-10deg); opacity:0; } 40% { transform: scale(1.3) rotate(5deg); opacity:1; } 100% { transform: scale(1) rotate(0deg); opacity:0; } }
@keyframes arena-coin-pop { 0% { transform: translateY(0) scale(0); opacity:0; } 50% { transform: translateY(-20px) scale(1.2); opacity:1; } 100% { transform: translateY(-40px) scale(1); opacity:0; } }
`;
if (typeof document !== "undefined" && !document.getElementById("arena-animations")) {
  const style = document.createElement("style");
  style.id = "arena-animations";
  style.textContent = flashKeyframes;
  document.head.appendChild(style);
}

const RANK_COLORS = {
  "Arena Master": "from-yellow-400 to-amber-500",
  "Legend": "from-purple-500 to-violet-600",
  "Champion": "from-blue-500 to-cyan-500",
  "Speedster": "from-green-500 to-emerald-500",
  "Learner": "from-orange-400 to-amber-500",
  "Beginner": "from-gray-400 to-gray-500"
};

const RANK_ICONS = {
  "Arena Master": Crown, "Legend": Star, "Champion": Trophy,
  "Speedster": Zap, "Learner": Target, "Beginner": Shield
};

// ===================== AVATAR CATALOG =====================
const AVATAR_CATALOG = {
  skin: [
    { id: "skin_default", label: "Default", color: "#F5D0A9", cost: 0 },
    { id: "skin_light", label: "Light", color: "#FFE0BD", cost: 0 },
    { id: "skin_medium", label: "Medium", color: "#D4A574", cost: 0 },
    { id: "skin_tan", label: "Tan", color: "#C68642", cost: 0 },
    { id: "skin_dark", label: "Dark", color: "#8D5524", cost: 0 },
    { id: "skin_blue", label: "Blue", color: "#5B9BD5", cost: 5 },
    { id: "skin_green", label: "Green", color: "#6BCB77", cost: 5 },
    { id: "skin_pink", label: "Pink", color: "#FF8FAB", cost: 5 },
    { id: "skin_purple", label: "Purple", color: "#B983FF", cost: 5 },
  ],
  hair: [
    { id: "hair_none", label: "None", cost: 0, render: () => null },
    { id: "hair_short", label: "Short", cost: 0, color: "#3B2417", render: (c) => <><path d="M30 28C30 22 38 15 55 15C72 15 80 22 80 28L80 35C78 30 70 24 55 24C40 24 32 30 30 35Z" fill={c}/><path d="M32 30C33 25 42 19 55 19C68 19 77 25 78 30L78 28C77 22 68 16 55 16C42 16 33 22 32 28Z" fill={c} opacity="0.5"/></> },
    { id: "hair_spiky", label: "Spiky", cost: 5, color: "#1a1a2e", render: (c) => <><path d="M35 32L40 10L48 28L55 5L62 28L70 10L75 32Z" fill={c}/><path d="M38 30L43 14L50 26L55 8L60 26L67 14L72 30Z" fill={c} opacity="0.7"/></> },
    { id: "hair_long", label: "Long Straight", cost: 5, color: "#8B4513", render: (c) => <><path d="M28 35C28 20 38 12 55 12C72 12 82 20 82 35L82 70C82 72 80 72 80 70L80 40C78 32 70 22 55 22C40 22 32 32 30 40L30 70C30 72 28 72 28 70Z" fill={c}/></> },
    { id: "hair_curly", label: "Curly", cost: 8, color: "#2C1810", render: (c) => <><circle cx="38" cy="22" r="8" fill={c}/><circle cx="50" cy="18" r="8" fill={c}/><circle cx="62" cy="18" r="8" fill={c}/><circle cx="72" cy="22" r="8" fill={c}/><circle cx="30" cy="30" r="7" fill={c}/><circle cx="80" cy="30" r="7" fill={c}/><circle cx="34" cy="26" r="7" fill={c}/><circle cx="76" cy="26" r="7" fill={c}/></> },
    { id: "hair_mohawk", label: "Mohawk", cost: 10, color: "#FF1744", render: (c) => <path d="M48 30L50 2L52 10L55 0L58 10L60 2L62 30Z" fill={c}/> },
    { id: "hair_pigtails", label: "Pigtails", cost: 8, color: "#F9A825", render: (c) => <><path d="M30 28C30 20 40 14 55 14C70 14 80 20 80 28L80 33C78 28 68 20 55 20C42 20 32 28 30 33Z" fill={c}/><ellipse cx="28" cy="50" rx="8" ry="14" fill={c}/><ellipse cx="82" cy="50" rx="8" ry="14" fill={c}/></> },
    { id: "hair_bob", label: "Bob", cost: 5, color: "#5C2D91", render: (c) => <path d="M28 32C28 20 38 13 55 13C72 13 82 20 82 32L82 52C82 55 78 55 78 52L78 38C76 30 68 22 55 22C42 22 34 30 32 38L32 52C32 55 28 55 28 52Z" fill={c}/> },
    { id: "hair_ponytail", label: "Ponytail", cost: 5, color: "#D4A574", render: (c) => <><path d="M30 28C30 20 40 14 55 14C70 14 80 20 80 28L80 33C78 28 68 20 55 20C42 20 32 28 30 33Z" fill={c}/><path d="M72 30C76 35 78 50 75 70C74 72 72 72 73 70C75 52 74 38 70 32" fill={c}/></> },
    { id: "hair_braids", label: "Braids", cost: 8, color: "#1a1a2e", render: (c) => <><path d="M30 28C30 20 40 14 55 14C70 14 80 20 80 28L80 33C78 28 68 20 55 20C42 20 32 28 30 33Z" fill={c}/><path d="M30 33C28 38 27 50 28 65" stroke={c} strokeWidth="6" fill="none"/><path d="M80 33C82 38 83 50 82 65" stroke={c} strokeWidth="6" fill="none"/><circle cx="28" cy="67" r="4" fill={c}/><circle cx="82" cy="67" r="4" fill={c}/></> },
    { id: "hair_bun", label: "Bun", cost: 5, color: "#6B3A2A", render: (c) => <><path d="M30 28C30 20 40 14 55 14C70 14 80 20 80 28L80 33C78 28 68 20 55 20C42 20 32 28 30 33Z" fill={c}/><circle cx="55" cy="12" r="10" fill={c}/></> },
    { id: "hair_wavy", label: "Wavy Long", cost: 8, color: "#C2185B", render: (c) => <><path d="M28 30C28 18 38 10 55 10C72 10 82 18 82 30" fill={c}/><path d="M28 30C26 40 28 55 30 68C32 72 30 74 28 70C25 58 24 42 28 30Z" fill={c}/><path d="M82 30C84 40 82 55 80 68C78 72 80 74 82 70C85 58 86 42 82 30Z" fill={c}/></> },
    { id: "hair_afro", label: "Afro", cost: 8, color: "#3E2723", render: (c) => <circle cx="55" cy="30" r="28" fill={c}/> },
    { id: "hair_buzz", label: "Buzz Cut", cost: 0, color: "#5D4037", render: (c) => <path d="M32 35C32 22 40 16 55 16C70 16 78 22 78 35Z" fill={c}/> },
    { id: "hair_sideshave", label: "Side Shave", cost: 10, color: "#9C27B0", render: (c) => <><path d="M35 35C35 22 42 16 55 16C68 16 75 22 75 35Z" fill={c}/><path d="M35 28L55 16L55 35L35 35Z" fill={c}/><path d="M78 35C78 28 74 22 68 20L78 35Z" fill={c} opacity="0.3"/></> },
  ],
  eyes: [
    { id: "eyes_normal", label: "Normal", cost: 0, render: () => <><circle cx="43" cy="45" r="4" fill="#1a1a2e"/><circle cx="67" cy="45" r="4" fill="#1a1a2e"/><circle cx="44.5" cy="43.5" r="1.5" fill="white"/><circle cx="68.5" cy="43.5" r="1.5" fill="white"/></> },
    { id: "eyes_happy", label: "Happy", cost: 0, render: () => <><path d="M38 45C40 42 46 42 48 45" stroke="#1a1a2e" strokeWidth="2.5" fill="none" strokeLinecap="round"/><path d="M62 45C64 42 70 42 72 45" stroke="#1a1a2e" strokeWidth="2.5" fill="none" strokeLinecap="round"/></> },
    { id: "eyes_cool", label: "Sunglasses", cost: 5, render: () => <><rect x="35" y="40" width="18" height="10" rx="2" fill="#1a1a2e"/><rect x="57" y="40" width="18" height="10" rx="2" fill="#1a1a2e"/><line x1="53" y1="45" x2="57" y2="45" stroke="#1a1a2e" strokeWidth="2"/><rect x="37" y="42" width="5" height="4" rx="1" fill="#4FC3F7" opacity="0.6"/><rect x="59" y="42" width="5" height="4" rx="1" fill="#4FC3F7" opacity="0.6"/></> },
    { id: "eyes_star", label: "Star Eyes", cost: 10, render: () => <><text x="43" y="49" textAnchor="middle" fontSize="14" fill="#FFD700">&#9733;</text><text x="67" y="49" textAnchor="middle" fontSize="14" fill="#FFD700">&#9733;</text></> },
    { id: "eyes_wink", label: "Wink", cost: 5, render: () => <><circle cx="43" cy="45" r="4" fill="#1a1a2e"/><circle cx="44.5" cy="43.5" r="1.5" fill="white"/><path d="M62 45C64 42 70 42 72 45" stroke="#1a1a2e" strokeWidth="2.5" fill="none" strokeLinecap="round"/></> },
    { id: "eyes_angry", label: "Fierce", cost: 5, render: () => <><circle cx="43" cy="46" r="3.5" fill="#1a1a2e"/><circle cx="67" cy="46" r="3.5" fill="#1a1a2e"/><line x1="36" y1="40" x2="50" y2="42" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round"/><line x1="74" y1="40" x2="60" y2="42" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round"/></> },
    { id: "eyes_lashes", label: "Lashes", cost: 5, render: () => <><circle cx="43" cy="45" r="4" fill="#1a1a2e"/><circle cx="67" cy="45" r="4" fill="#1a1a2e"/><circle cx="44.5" cy="43.5" r="1.5" fill="white"/><circle cx="68.5" cy="43.5" r="1.5" fill="white"/><line x1="36" y1="41" x2="39" y2="39" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round"/><line x1="38" y1="40" x2="40" y2="37" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round"/><line x1="40" y1="39" x2="42" y2="37" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round"/><line x1="68" y1="39" x2="70" y2="37" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round"/><line x1="70" y1="40" x2="72" y2="37" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round"/><line x1="72" y1="41" x2="74" y2="39" stroke="#1a1a2e" strokeWidth="1.5" strokeLinecap="round"/></> },
    { id: "eyes_hearts", label: "Heart Eyes", cost: 10, render: () => <><text x="43" y="49" textAnchor="middle" fontSize="13" fill="#FF1744">&#9829;</text><text x="67" y="49" textAnchor="middle" fontSize="13" fill="#FF1744">&#9829;</text></> },
    { id: "eyes_cat", label: "Cat Eyes", cost: 8, render: () => <><ellipse cx="43" cy="45" rx="5" ry="4" fill="#4CAF50"/><ellipse cx="67" cy="45" rx="5" ry="4" fill="#4CAF50"/><ellipse cx="43" cy="45" rx="1.5" ry="4" fill="#1a1a2e"/><ellipse cx="67" cy="45" rx="1.5" ry="4" fill="#1a1a2e"/></> },
    { id: "eyes_round", label: "Round Glasses", cost: 5, render: () => <><circle cx="43" cy="45" r="9" fill="none" stroke="#8B6914" strokeWidth="2"/><circle cx="67" cy="45" r="9" fill="none" stroke="#8B6914" strokeWidth="2"/><line x1="52" y1="45" x2="58" y2="45" stroke="#8B6914" strokeWidth="2"/><circle cx="43" cy="45" r="3" fill="#1a1a2e"/><circle cx="67" cy="45" r="3" fill="#1a1a2e"/></> },
  ],
  mouth: [
    { id: "mouth_smile", label: "Smile", cost: 0, render: () => <path d="M44 58C48 64 62 64 66 58" stroke="#1a1a2e" strokeWidth="2" fill="none" strokeLinecap="round"/> },
    { id: "mouth_grin", label: "Grin", cost: 0, render: () => <path d="M42 57C46 65 64 65 68 57Z" fill="#1a1a2e"/> },
    { id: "mouth_tongue", label: "Tongue", cost: 5, render: () => <><path d="M42 57C46 65 64 65 68 57Z" fill="#1a1a2e"/><ellipse cx="55" cy="64" rx="5" ry="4" fill="#FF6B6B"/></> },
    { id: "mouth_smirk", label: "Smirk", cost: 5, render: () => <path d="M46 60C52 64 62 62 68 58" stroke="#1a1a2e" strokeWidth="2" fill="none" strokeLinecap="round"/> },
    { id: "mouth_open", label: "Wow", cost: 5, render: () => <ellipse cx="55" cy="60" rx="6" ry="5" fill="#1a1a2e"/> },
    { id: "mouth_vampire", label: "Fangs", cost: 10, render: () => <><path d="M42 57C46 64 64 64 68 57Z" fill="#1a1a2e"/><path d="M46 57L48 62L50 57" fill="white"/><path d="M60 57L62 62L64 57" fill="white"/></> },
    { id: "mouth_lipstick", label: "Lipstick", cost: 5, render: () => <path d="M44 58C48 64 62 64 66 58" stroke="#E91E63" strokeWidth="3" fill="none" strokeLinecap="round"/> },
    { id: "mouth_kiss", label: "Kiss", cost: 8, render: () => <><circle cx="55" cy="60" r="4" fill="#E91E63"/><path d="M51 58C53 56 57 56 59 58" fill="#E91E63"/></> },
    { id: "mouth_braces", label: "Braces", cost: 5, render: () => <><path d="M42 57C46 64 64 64 68 57Z" fill="white" stroke="#1a1a2e" strokeWidth="1"/><line x1="44" y1="60" x2="66" y2="60" stroke="#78909C" strokeWidth="1.5"/><circle cx="48" cy="60" r="1" fill="#78909C"/><circle cx="55" cy="60" r="1" fill="#78909C"/><circle cx="62" cy="60" r="1" fill="#78909C"/></> },
  ],
  outfit: [
    { id: "outfit_tshirt", label: "T-Shirt", cost: 0, color: "#3B82F6", render: (c) => <path d="M35 75L30 90L30 110L80 110L80 90L75 75Z" fill={c}/> },
    { id: "outfit_hoodie", label: "Hoodie", cost: 8, color: "#7C3AED", render: (c) => <><path d="M33 72L25 90L25 110L85 110L85 90L77 72Z" fill={c}/><path d="M42 72L48 85L55 78L62 85L68 72" fill={c} stroke={c} strokeWidth="1"/><path d="M40 95L50 95L50 110L40 110Z" fill={c} stroke="rgba(0,0,0,0.15)" strokeWidth="1"/></> },
    { id: "outfit_jacket", label: "Jacket", cost: 10, color: "#1a1a2e", render: (c) => <><path d="M33 72L25 90L25 110L85 110L85 90L77 72Z" fill={c}/><line x1="55" y1="75" x2="55" y2="110" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/><circle cx="52" cy="85" r="1.5" fill="#FFD700"/><circle cx="52" cy="93" r="1.5" fill="#FFD700"/><circle cx="52" cy="101" r="1.5" fill="#FFD700"/></> },
    { id: "outfit_jersey", label: "Jersey", cost: 8, color: "#EF4444", render: (c) => <><path d="M35 75L28 90L28 110L82 110L82 90L75 75Z" fill={c}/><text x="55" y="98" textAnchor="middle" fontSize="16" fill="white" fontWeight="bold">7</text></> },
    { id: "outfit_dress", label: "Dress", cost: 8, color: "#EC4899", render: (c) => <><path d="M38 75L30 95L25 110L85 110L80 95L72 75Z" fill={c}/><path d="M38 75L55 80L72 75L68 85L55 88L42 85Z" fill={c} opacity="0.8"/></> },
    { id: "outfit_rocker", label: "Rock Star", cost: 15, color: "#000000", render: (c) => <><path d="M33 72L25 90L25 110L85 110L85 90L77 72Z" fill={c}/><path d="M40 80L45 90L55 85L65 90L70 80" stroke="#FFD700" strokeWidth="2" fill="none"/><circle cx="55" cy="95" r="4" fill="none" stroke="#FFD700" strokeWidth="1.5"/></> },
    { id: "outfit_sundress", label: "Sundress", cost: 8, color: "#FFC107", render: (c) => <><path d="M40 75L32 95L28 110L82 110L78 95L70 75Z" fill={c}/><path d="M40 75L55 78L70 75" stroke={c} strokeWidth="3" fill="none"/><circle cx="45" cy="90" r="2" fill="white" opacity="0.5"/><circle cx="55" cy="85" r="2" fill="white" opacity="0.5"/><circle cx="65" cy="92" r="2" fill="white" opacity="0.5"/><circle cx="50" cy="100" r="2" fill="white" opacity="0.5"/></> },
    { id: "outfit_polo", label: "Polo", cost: 5, color: "#2E7D32", render: (c) => <><path d="M35 75L30 90L30 110L80 110L80 90L75 75Z" fill={c}/><path d="M48 75L48 82L55 80L62 82L62 75" fill={c} stroke="white" strokeWidth="1"/></> },
    { id: "outfit_overalls", label: "Overalls", cost: 10, color: "#5C6BC0", render: (c) => <><path d="M35 80L30 95L30 110L80 110L80 95L75 80Z" fill={c}/><path d="M40 75L40 85" stroke={c} strokeWidth="5"/><path d="M70 75L70 85" stroke={c} strokeWidth="5"/><rect x="42" y="90" rx="2" width="26" height="12" fill={c} stroke="rgba(255,255,255,0.3)" strokeWidth="1"/></> },
    { id: "outfit_princess", label: "Princess", cost: 12, color: "#CE93D8", render: (c) => <><path d="M38 75L28 98L22 110L88 110L82 98L72 75Z" fill={c}/><path d="M38 75L55 80L72 75L65 82L55 85L45 82Z" fill={c} opacity="0.7"/><path d="M30 105L80 105" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeDasharray="3 3"/></> },
    { id: "outfit_tank", label: "Tank Top", cost: 0, color: "#F44336", render: (c) => <path d="M40 75L35 90L35 110L75 110L75 90L70 75Z" fill={c}/> },
  ],
  accessory: [
    { id: "acc_none", label: "None", cost: 0, render: () => null },
    { id: "acc_headband", label: "Headband", cost: 5, render: () => <rect x="30" y="32" width="50" height="5" rx="2" fill="#EF4444"/> },
    { id: "acc_crown", label: "Crown", cost: 15, render: () => <><path d="M35 28L40 18L47 25L55 12L63 25L70 18L75 28Z" fill="#FFD700"/><rect x="35" y="28" width="40" height="5" rx="1" fill="#FFD700"/><circle cx="47" cy="30" r="2" fill="#EF4444"/><circle cx="55" cy="30" r="2" fill="#3B82F6"/><circle cx="63" cy="30" r="2" fill="#10B981"/></> },
    { id: "acc_headphones", label: "Headphones", cost: 10, render: () => <><path d="M28 42C28 28 38 18 55 18C72 18 82 28 82 42" stroke="#333" strokeWidth="4" fill="none"/><rect x="23" y="38" width="10" height="14" rx="5" fill="#333"/><rect x="77" y="38" width="10" height="14" rx="5" fill="#333"/><rect x="25" y="40" width="6" height="10" rx="3" fill="#555"/><rect x="79" y="40" width="6" height="10" rx="3" fill="#555"/></> },
    { id: "acc_guitar", label: "Guitar", cost: 12, render: () => <><line x1="85" y1="65" x2="100" y2="95" stroke="#8B4513" strokeWidth="3"/><ellipse cx="100" cy="100" rx="10" ry="8" fill="#D4A574"/><ellipse cx="100" cy="100" rx="4" ry="3" fill="#1a1a2e"/></> },
    { id: "acc_cape", label: "Cape", cost: 12, render: () => <path d="M35 72L20 110L55 105L90 110L75 72Z" fill="#DC2626" opacity="0.8"/> },
    { id: "acc_scarf", label: "Scarf", cost: 5, render: () => <><path d="M38 70C38 70 45 75 55 75C65 75 72 70 72 70L72 80C72 80 65 78 55 78C45 78 38 80 38 80Z" fill="#10B981"/><path d="M38 78L32 95" stroke="#10B981" strokeWidth="5" strokeLinecap="round"/></> },
    { id: "acc_tiara", label: "Tiara", cost: 12, render: () => <><path d="M38 30L42 22L47 28L52 20L55 14L58 20L63 28L68 22L72 30" stroke="#FFD700" strokeWidth="2" fill="none"/><circle cx="55" cy="15" r="3" fill="#E040FB"/><circle cx="47" cy="23" r="2" fill="#4FC3F7"/><circle cx="63" cy="23" r="2" fill="#4FC3F7"/></> },
    { id: "acc_bow", label: "Hair Bow", cost: 5, render: () => <><path d="M50 18L42 12L50 20L42 28Z" fill="#FF4081"/><path d="M60 18L68 12L60 20L68 28Z" fill="#FF4081"/><circle cx="55" cy="20" r="3" fill="#E91E63"/></> },
    { id: "acc_flower", label: "Flower", cost: 5, render: () => <><circle cx="78" cy="35" r="3" fill="#FF9800"/><circle cx="82" cy="32" r="4" fill="#FF5722"/><circle cx="85" cy="36" r="4" fill="#E91E63"/><circle cx="82" cy="40" r="4" fill="#FF4081"/><circle cx="78" cy="38" r="4" fill="#FF6F00"/><circle cx="81" cy="36" r="2.5" fill="#FFEB3B"/></> },
    { id: "acc_necklace", label: "Necklace", cost: 8, render: () => <><path d="M40 70C40 70 47 76 55 76C63 76 70 70 70 70" stroke="#FFD700" strokeWidth="1.5" fill="none"/><circle cx="55" cy="77" r="3" fill="#E040FB"/><circle cx="55" cy="77" r="1.5" fill="#CE93D8"/></> },
    { id: "acc_earrings", label: "Earrings", cost: 8, render: () => <><circle cx="28" cy="52" r="3" fill="#FFD700"/><circle cx="82" cy="52" r="3" fill="#FFD700"/><circle cx="28" cy="56" r="2" fill="#E040FB"/><circle cx="82" cy="56" r="2" fill="#E040FB"/></> },
    { id: "acc_cap", label: "Cap", cost: 5, render: () => <><path d="M30 32C30 22 40 16 55 16C70 16 80 22 80 32L80 35L30 35Z" fill="#1565C0"/><path d="M28 35L85 35L85 38L28 38Z" fill="#0D47A1"/></> },
  ],
  background: [
    { id: "bg_none", label: "Plain", cost: 0, color: "#F1F5F9" },
    { id: "bg_blue", label: "Sky Blue", cost: 3, color: "#DBEAFE" },
    { id: "bg_sunset", label: "Sunset", cost: 5, gradient: ["#FF9A8B","#FF6A88","#FF99AC"] },
    { id: "bg_ocean", label: "Ocean", cost: 5, gradient: ["#667eea","#764ba2"] },
    { id: "bg_forest", label: "Forest", cost: 5, gradient: ["#134E5E","#71B280"] },
    { id: "bg_night", label: "Night", cost: 8, gradient: ["#0F2027","#203A43","#2C5364"] },
    { id: "bg_gold", label: "Gold", cost: 10, gradient: ["#F09819","#EDDE5D"] },
    { id: "bg_neon", label: "Neon", cost: 10, gradient: ["#a855f7","#ec4899","#f97316"] },
  ],
};

const CATEGORY_LABELS = { skin: "Skin", hair: "Hair", eyes: "Eyes", mouth: "Mouth", outfit: "Outfit", accessory: "Extras", background: "Background" };
const CATEGORY_ORDER = ["skin","hair","eyes","mouth","outfit","accessory","background"];

function AvatarRenderer({ avatar, size = 110 }) {
  const a = avatar || {};
  const skin = AVATAR_CATALOG.skin.find(s => s.id === a.skin) || AVATAR_CATALOG.skin[0];
  const hair = AVATAR_CATALOG.hair.find(h => h.id === a.hair) || AVATAR_CATALOG.hair[0];
  const eyes = AVATAR_CATALOG.eyes.find(e => e.id === a.eyes) || AVATAR_CATALOG.eyes[0];
  const mouth = AVATAR_CATALOG.mouth.find(m => m.id === a.mouth) || AVATAR_CATALOG.mouth[0];
  const outfit = AVATAR_CATALOG.outfit.find(o => o.id === a.outfit) || AVATAR_CATALOG.outfit[0];
  const acc = AVATAR_CATALOG.accessory.find(x => x.id === a.accessory) || AVATAR_CATALOG.accessory[0];
  const bg = AVATAR_CATALOG.background.find(b => b.id === a.background) || AVATAR_CATALOG.background[0];

  const bgId = `bg_grad_${bg.id}`;
  return (
    <svg width={size} height={size} viewBox="0 0 110 110" data-testid="avatar-svg">
      <defs>{bg.gradient && <linearGradient id={bgId} x1="0%" y1="0%" x2="100%" y2="100%">{bg.gradient.map((c,i) => <stop key={i} offset={`${(i/(bg.gradient.length-1))*100}%`} stopColor={c}/>)}</linearGradient>}</defs>
      <rect width="110" height="110" rx="20" fill={bg.gradient ? `url(#${bgId})` : bg.color || "#F1F5F9"}/>
      {acc.id === "acc_cape" && acc.render()}
      {outfit.render(a.outfitColor || outfit.color)}
      <ellipse cx="55" cy="50" rx="25" ry="28" fill={skin.color}/>
      {eyes.render()}
      {mouth.render()}
      {hair.render(a.hairColor || hair.color)}
      {acc.id !== "acc_none" && acc.id !== "acc_cape" && acc.render()}
    </svg>
  );
}

function AvatarDisplay({ avatar, size = "w-16 h-16" }) {
  const dim = parseInt(size.match(/\d+/)?.[0]) * 4 || 64;
  return (
    <div className={`${size} rounded-2xl overflow-hidden shadow-lg`} data-testid="avatar-display">
      <AvatarRenderer avatar={avatar} size={dim} />
    </div>
  );
}

// ===================== NUMBER PAD =====================

function NumberPad({ value, onChange, onSubmit }) {
  const press = (digit) => onChange(value + digit);
  const backspace = () => onChange(value.slice(0, -1));

  return (
    <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto mt-3" data-testid="number-pad">
      {[1,2,3,4,5,6,7,8,9].map(n => (
        <button key={n} onClick={() => press(String(n))} data-testid={`pad-${n}`}
          className="h-14 sm:h-16 rounded-xl bg-primary/10 hover:bg-primary/20 active:scale-95 text-2xl font-bold text-foreground transition-all">
          {n}
        </button>
      ))}
      <button onClick={backspace} data-testid="pad-backspace"
        className="h-14 sm:h-16 rounded-xl bg-red-500/15 hover:bg-red-500/25 active:scale-95 text-xl font-bold text-red-500 transition-all flex items-center justify-center">
        <ArrowLeft className="w-6 h-6" />
      </button>
      <button onClick={() => press("0")} data-testid="pad-0"
        className="h-14 sm:h-16 rounded-xl bg-primary/10 hover:bg-primary/20 active:scale-95 text-2xl font-bold text-foreground transition-all">
        0
      </button>
      <button onClick={onSubmit} data-testid="pad-submit"
        className="h-14 sm:h-16 rounded-xl bg-green-500 hover:bg-green-600 active:scale-95 text-white transition-all flex items-center justify-center">
        <Check className="w-7 h-7" />
      </button>
    </div>
  );
}

// ===================== LIVE BATTLE =====================

function LiveBattle({ user, classes, onBack, onComplete }) {
  const [tables, setTables] = useState([2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]);
  const [phase, setPhase] = useState("setup"); // setup, waiting, playing, ended
  const [ws, setWs] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [qStartTime, setQStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [myScore, setMyScore] = useState(0);
  const [myAnswered, setMyAnswered] = useState(0);
  const [opponentName, setOpponentName] = useState("");
  const [opponentScore, setOpponentScore] = useState(0);
  const [opponentAnswered, setOpponentAnswered] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [streak, setStreak] = useState(0);
  const [battleResult, setBattleResult] = useState(null);
  const inputRef = useRef(null);

  const toggleTable = (t) => {
    setTables(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort((a,b) => a - b));
  };

  const startLiveBattle = () => {
    if (tables.length === 0) { toast.error("Select at least one table"); return; }

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://");
    const token = localStorage.getItem("token");

    const socket = new WebSocket(`${wsUrl}/api/ws/arena/live`);
    setWs(socket);
    setPhase("waiting");

    socket.onopen = () => {
      socket.send(JSON.stringify({ token, name: user.name, tables }));
    };

    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "waiting") {
        setPhase("waiting");
      } else if (msg.type === "battle_start") {
        setQuestions(msg.questions);
        setOpponentName(msg.opponent_name);
        setPhase("playing");
        setCurrentQ(0);
        setMyScore(0);
        setMyAnswered(0);
        setOpponentScore(0);
        setOpponentAnswered(0);
        setQStartTime(Date.now());
        setTimeLeft(60);
      } else if (msg.type === "answer_result") {
        const correct = msg.correct;
        if (correct) {
          const ns = streak + 1;
          setStreak(ns);
          setMyScore(s => s + 1);
          if (ns >= 3 && ns % 3 === 0) { playStreak(); setFeedback({ type: "streak" }); }
          else { playCorrect(); setFeedback({ type: "correct" }); }
        } else {
          setStreak(0);
          playWrong();
          setFeedback({ type: "wrong" });
        }
        setMyAnswered(a => a + 1);
        setTimeout(() => setFeedback(null), 400);
      } else if (msg.type === "progress") {
        const players = msg.players;
        for (const [uid, p] of Object.entries(players)) {
          if (uid !== user.user_id) {
            setOpponentScore(p.score);
            setOpponentAnswered(p.answered);
          }
        }
      } else if (msg.type === "timer") {
        setTimeLeft(msg.remaining);
      } else if (msg.type === "battle_end") {
        setBattleResult(msg);
        setPhase("ended");
        if (onComplete) onComplete();
      } else if (msg.type === "opponent_disconnected") {
        toast.info("Opponent disconnected");
      } else if (msg.type === "error") {
        toast.error(msg.message);
        setPhase("setup");
      }
    };

    socket.onclose = () => {
      if (phase !== "ended") setPhase("setup");
    };
    socket.onerror = () => toast.error("Connection error");
  };

  const submitAnswer = () => {
    if (!userAnswer.trim() || !ws || phase !== "playing") return;
    const timeMs = Date.now() - qStartTime;
    const d = questions[currentQ]?.display || "";
    let expected = 0;
    if (d.includes("×")) { const [a,b] = d.split("×").map(s => parseInt(s.trim())); expected = a * b; }
    else if (d.includes("÷")) { const [a,b] = d.split("÷").map(s => parseInt(s.trim())); expected = Math.round(a / b); }

    ws.send(JSON.stringify({ type: "answer", index: currentQ, user_answer: parseInt(userAnswer), time_ms: timeMs }));
    setUserAnswer("");
    if (currentQ + 1 < questions.length) {
      setCurrentQ(currentQ + 1);
      setQStartTime(Date.now());
    }
  };

  const cancelBattle = () => {
    if (ws) { try { ws.send(JSON.stringify({ type: "cancel" })); ws.close(); } catch {} }
    setPhase("setup");
    setWs(null);
  };

  useEffect(() => { if (phase === "playing" && inputRef.current) inputRef.current.focus(); }, [currentQ, phase]);
  useEffect(() => { return () => { if (ws) try { ws.close(); } catch {} }; }, []);

  // Setup screen
  if (phase === "setup") {
    return (
      <div className="space-y-6" data-testid="live-battle-setup">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
          <h2 className="text-2xl font-bold font-['Manrope']">Live Battle</h2>
        </div>
        <p className="text-muted-foreground">Select your tables, then wait for any opponent to join!</p>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {[2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(t => (
            <button key={t} onClick={() => toggleTable(t)}
              className={`h-12 rounded-xl font-bold text-lg transition-all ${tables.includes(t) ? "bg-primary text-primary-foreground shadow-md scale-105" : "bg-muted text-muted-foreground"}`}>{t}x</button>
          ))}
        </div>
        <Button className="w-full h-14 text-lg font-bold rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:opacity-90" onClick={startLiveBattle} data-testid="find-opponent-btn">
          <Swords className="w-5 h-5 mr-2" />Find Opponent
        </Button>
      </div>
    );
  }

  // Waiting screen
  if (phase === "waiting") {
    return (
      <div className="text-center py-12 space-y-6" data-testid="live-waiting">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mx-auto animate-pulse">
          <Swords className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-bold">Waiting for an opponent...</h2>
        <p className="text-muted-foreground">Ask any student to join Times Tables Arena and click Live Battle!</p>
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <Button variant="outline" onClick={cancelBattle}>Cancel</Button>
      </div>
    );
  }

  // Battle ended
  if (phase === "ended" && battleResult) {
    const myResult = battleResult.players[user.user_id];
    const opResult = Object.entries(battleResult.players).find(([uid]) => uid !== user.user_id)?.[1];
    const iWon = myResult?.is_winner;
    return (
      <div className="space-y-6 text-center" data-testid="live-battle-result">
        <div className={`text-4xl font-bold ${iWon ? "text-green-500" : "text-orange-500"}`}>
          {iWon ? "You Won!" : "You Lost!"}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Card className={iWon ? "ring-2 ring-green-500" : ""}>
            <CardContent className="p-4 text-center">
              <p className="font-bold">{myResult?.name || "You"}</p>
              <p className="text-3xl font-bold mt-2">{myResult?.score || 0}</p>
              <p className="text-xs text-muted-foreground">{myResult?.avg_time_ms ? `${(myResult.avg_time_ms/1000).toFixed(1)}s avg` : ""}</p>
              <p className="text-yellow-500 font-bold text-sm mt-1">+{myResult?.coins_earned || 0} coins</p>
            </CardContent>
          </Card>
          <Card className={!iWon ? "ring-2 ring-green-500" : ""}>
            <CardContent className="p-4 text-center">
              <p className="font-bold">{opResult?.name || "Opponent"}</p>
              <p className="text-3xl font-bold mt-2">{opResult?.score || 0}</p>
              <p className="text-xs text-muted-foreground">{opResult?.avg_time_ms ? `${(opResult.avg_time_ms/1000).toFixed(1)}s avg` : ""}</p>
              <p className="text-yellow-500 font-bold text-sm mt-1">+{opResult?.coins_earned || 0} coins</p>
            </CardContent>
          </Card>
        </div>
        <Button onClick={onBack} className="w-full" data-testid="back-to-menu-btn"><ArrowLeft className="w-4 h-4 mr-2" />Back to Menu</Button>
      </div>
    );
  }

  // Active live gameplay
  if (phase === "playing" && questions.length > 0) {
    const q = questions[currentQ];
    const progress = (myAnswered / questions.length) * 100;
    const opProgress = (opponentAnswered / questions.length) * 100;
    return (
      <div className="space-y-4" data-testid="live-battle-gameplay">
        {/* Score bar */}
        <div className="flex items-center justify-between bg-muted/30 rounded-xl p-3">
          <div className="text-center flex-1">
            <p className="text-xs text-muted-foreground">You</p>
            <p className="text-2xl font-bold text-green-500">{myScore}</p>
          </div>
          <div className="text-center px-4">
            <span className={`text-lg font-bold font-mono ${timeLeft <= 10 ? "text-red-500 animate-pulse" : "text-muted-foreground"}`}>
              {timeLeft}s
            </span>
          </div>
          <div className="text-center flex-1">
            <p className="text-xs text-muted-foreground">{opponentName}</p>
            <p className="text-2xl font-bold text-orange-500">{opponentScore}</p>
          </div>
        </div>
        {/* Dual progress bars */}
        <div className="space-y-1">
          <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-10">You</span><div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div></div>
          <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-10">{opponentName.split(" ")[0]}</span><div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-orange-500 rounded-full transition-all duration-300" style={{ width: `${opProgress}%` }} /></div></div>
        </div>
        {/* Streak */}
        {streak >= 3 && <p className="text-center text-orange-500 font-bold text-sm"><Flame className="w-4 h-4 inline mr-1" />{streak} streak!</p>}
        {/* Question */}
        {q && (
          <div className="relative text-center py-6 rounded-2xl" style={{
            animation: feedback?.type === "correct" || feedback?.type === "streak" ? "arena-correct 0.4s ease-out" : feedback?.type === "wrong" ? "arena-wrong 0.4s ease-out" : "none"
          }}>
            <p className="text-4xl sm:text-5xl font-bold font-mono">{q.display} = ?</p>
            {feedback?.type === "streak" && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ animation: "arena-streak-fire 0.6s ease-out forwards" }}><span className="text-5xl">&#128293;</span></div>}
          </div>
        )}
        {/* Input */}
        <div className="flex gap-2 max-w-xs mx-auto">
          <input ref={inputRef} type="number" value={userAnswer} onChange={e => setUserAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitAnswer(); }}
            className={`flex-1 h-14 text-2xl font-bold text-center rounded-xl border-2 bg-background focus:outline-none transition-colors ${
              feedback?.type === "correct" || feedback?.type === "streak" ? "border-green-500" : feedback?.type === "wrong" ? "border-red-500" : "border-border focus:border-primary"
            }`} placeholder="?" autoFocus data-testid="live-answer-input" />
          <Button className="h-14 w-14 rounded-xl hidden sm:flex" onClick={submitAnswer}><ChevronRight className="w-6 h-6" /></Button>
        </div>
        <NumberPad value={userAnswer} onChange={(v) => setUserAnswer(v)} onSubmit={submitAnswer} />
      </div>
    );
  }

  return null;
}


// ===================== PLAY MODE =====================

function PlayMode({ user, profile, classes, onComplete }) {
  const [mode, setMode] = useState(null); // null=select, practice, speed_challenge, live_battle
  const [tables, setTables] = useState([2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]);
  const [session, setSession] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [qStartTime, setQStartTime] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type: "correct"|"wrong", streak: number}
  const [streak, setStreak] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const toggleTable = (t) => {
    setTables(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort((a,b)=>a-b));
  };

  const startSession = async () => {
    if (tables.length === 0) { toast.error("Select at least one table"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/arena/start`, { mode, tables, count: mode === "speed_challenge" ? 50 : 20 }, { headers });
      setSession(res.data);
      setCurrentQ(0);
      setAnswers([]);
      setResults(null);
      setStreak(0);
      setFeedback(null);
      setQStartTime(Date.now());
      if (mode === "speed_challenge") { setTimeLeft(60); }
      if (res.data.weak_facts_count > 0) {
        toast.info(`Targeting ${res.data.weak_facts_count} weak fact${res.data.weak_facts_count > 1 ? "s" : ""} for extra practice`);
      }
    } catch (e) { toast.error("Failed to start"); }
    setLoading(false);
  };

  useEffect(() => {
    if (mode === "speed_challenge" && session && !results) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [mode, session, results]);

  useEffect(() => {
    if (timeLeft === 0 && session && !results) submitSession();
  }, [timeLeft]);

  useEffect(() => { if (session && inputRef.current) inputRef.current.focus(); }, [currentQ, session]);

  // Parse the expected answer from the display string for instant feedback
  const getExpectedAnswer = (q) => {
    if (!q) return null;
    const d = q.display;
    if (d.includes("×")) {
      const [a, b] = d.split("×").map(s => parseInt(s.trim()));
      return a * b;
    }
    if (d.includes("÷")) {
      const [a, b] = d.split("÷").map(s => parseInt(s.trim()));
      return Math.round(a / b);
    }
    return null;
  };

  const submitAnswer = () => {
    if (!userAnswer.trim()) return;
    const timeMs = Date.now() - qStartTime;
    const parsed = parseInt(userAnswer);
    const expected = getExpectedAnswer(session.questions[currentQ]);
    const isCorrect = parsed === expected;

    // Sound + visual feedback
    if (isCorrect) {
      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak >= 3 && newStreak % 3 === 0) {
        playStreak();
        setFeedback({ type: "streak", streak: newStreak });
      } else {
        playCorrect();
        setFeedback({ type: "correct", streak: newStreak });
      }
    } else {
      setStreak(0);
      playWrong();
      setFeedback({ type: "wrong", streak: 0 });
    }

    // Clear feedback after animation
    setTimeout(() => setFeedback(null), 500);

    const newAnswers = [...answers, { index: currentQ, user_answer: parsed, time_ms: timeMs }];
    setAnswers(newAnswers);
    setUserAnswer("");
    if (currentQ + 1 < session.questions.length) {
      setCurrentQ(currentQ + 1);
      setQStartTime(Date.now());
    } else {
      submitSessionWithAnswers(newAnswers);
    }
  };

  const submitSession = () => submitSessionWithAnswers(answers);

  const submitSessionWithAnswers = async (finalAnswers) => {
    if (timerRef.current) clearInterval(timerRef.current);
    try {
      const res = await axios.post(`${API}/arena/submit`, { session_id: session.session_id, answers: finalAnswers }, { headers });
      setResults(res.data);
      if (onComplete) onComplete();
    } catch (e) { toast.error("Failed to submit"); }
  };

  // Mode selection
  if (!mode) {
    return (
      <div className="space-y-6" data-testid="arena-mode-select">
        <h2 className="text-2xl font-bold font-['Manrope']">Choose Your Mode</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg group" onClick={() => setMode("practice")} data-testid="mode-practice">
            <CardContent className="p-6 text-center space-y-3">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform"><Target className="w-8 h-8 text-white" /></div>
              <h3 className="text-lg font-bold">Practice</h3>
              <p className="text-sm text-muted-foreground">No time pressure. Focus on your weak spots. 20 questions.</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg group" onClick={() => setMode("speed_challenge")} data-testid="mode-speed">
            <CardContent className="p-6 text-center space-y-3">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform"><Timer className="w-8 h-8 text-white" /></div>
              <h3 className="text-lg font-bold">Speed Challenge</h3>
              <p className="text-sm text-muted-foreground">60 seconds. Answer as many as you can. Race the clock!</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg group" onClick={() => setMode("live_battle")} data-testid="mode-live">
            <CardContent className="p-6 text-center space-y-3">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform"><Swords className="w-8 h-8 text-white" /></div>
              <h3 className="text-lg font-bold">Live Battle</h3>
              <p className="text-sm text-muted-foreground">Real-time 1v1. Same questions. See who's faster!</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Live Battle mode — delegate to LiveBattle component
  if (mode === "live_battle") {
    return <LiveBattle user={user} classes={classes || []} onBack={() => setMode(null)} onComplete={onComplete} />;
  }

  // Table selection
  if (!session) {
    return (
      <div className="space-y-6" data-testid="arena-table-select">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setMode(null)}><ArrowLeft className="w-5 h-5" /></Button>
          <h2 className="text-2xl font-bold font-['Manrope']">{mode === "practice" ? "Practice" : "Speed Challenge"}</h2>
        </div>
        <p className="text-muted-foreground">Select which times tables to practice:</p>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {[2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(t => (
            <button key={t} onClick={() => toggleTable(t)} data-testid={`table-${t}`}
              className={`h-12 rounded-xl font-bold text-lg transition-all ${tables.includes(t) ? "bg-primary text-primary-foreground shadow-md scale-105" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {t}x
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setTables([2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20])}>All</Button>
          <Button variant="outline" size="sm" onClick={() => setTables([])}>None</Button>
          <Button variant="outline" size="sm" onClick={() => setTables([2,3,4,5])}>Easy (2-5)</Button>
          <Button variant="outline" size="sm" onClick={() => setTables([6,7,8,9])}>Medium (6-9)</Button>
          <Button variant="outline" size="sm" onClick={() => setTables([10,11,12])}>Hard (10-12)</Button>
          <Button variant="outline" size="sm" onClick={() => setTables([13,14,15,16,17,18,19,20])}>Expert (13-20)</Button>
        </div>
        <Button className="w-full h-14 text-lg font-bold rounded-xl" onClick={startSession} disabled={loading || tables.length === 0} data-testid="start-session-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Zap className="w-5 h-5 mr-2" />}
          Start!
        </Button>
      </div>
    );
  }

  // Results screen
  if (results) {
    const pct = results.total > 0 ? Math.round((results.correct / results.total) * 100) : 0;
    const RankIcon = RANK_ICONS[results.rank] || Shield;
    return (
      <div className="space-y-6 text-center" data-testid="arena-results">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${RANK_COLORS[results.rank] || "from-gray-400 to-gray-500"} text-white font-bold text-lg`}>
          <RankIcon className="w-5 h-5" /> {results.rank}
        </div>
        <h2 className="text-3xl font-bold font-['Manrope']">{pct >= 80 ? "Amazing!" : pct >= 60 ? "Good job!" : "Keep practicing!"}</h2>
        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-green-500">{results.correct}</p><p className="text-xs text-muted-foreground">Correct</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold">{results.avg_time_ms < 1000 ? `${results.avg_time_ms}ms` : `${(results.avg_time_ms/1000).toFixed(1)}s`}</p><p className="text-xs text-muted-foreground">Avg Speed</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-yellow-500">+{results.coins_earned}</p><p className="text-xs text-muted-foreground">Coins</p></CardContent></Card>
        </div>
        {results.streak_days > 1 && <p className="text-sm text-orange-500 font-medium"><Flame className="w-4 h-4 inline mr-1" />{results.streak_days} day streak!</p>}
        {/* Wrong answers review */}
        {results.results?.filter(r => !r.correct).length > 0 && (
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Review Wrong Answers</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {results.results.filter(r => !r.correct).map((r, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b border-border/30 last:border-0">
                  <span>{r.display}</span>
                  <span><span className="text-red-500 line-through mr-2">{r.user_answer}</span><span className="text-green-500 font-bold">{r.correct_answer}</span></span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        <div className="flex gap-3">
          <Button className="flex-1" onClick={() => { setSession(null); setResults(null); setAnswers([]); setCurrentQ(0); }} data-testid="play-again-btn"><RotateCcw className="w-4 h-4 mr-2" />Play Again</Button>
          <Button variant="outline" className="flex-1" onClick={() => { setMode(null); setSession(null); setResults(null); }}>Back to Menu</Button>
        </div>
      </div>
    );
  }

  // Active gameplay
  const q = session.questions[currentQ];
  const progress = ((currentQ) / session.questions.length) * 100;
  return (
    <div className="space-y-4" data-testid="arena-gameplay">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground font-medium">Q {currentQ + 1}/{session.questions.length}</span>
        {mode === "speed_challenge" && (
          <span className={`text-lg font-bold font-mono ${timeLeft <= 10 ? "text-red-500 animate-pulse" : ""}`} data-testid="timer">
            <Timer className="w-4 h-4 inline mr-1" />{timeLeft}s
          </span>
        )}
        <div className="flex items-center gap-2">
          {streak >= 3 && (
            <span className="flex items-center gap-1 text-orange-500 font-bold text-sm" data-testid="streak-display">
              <Flame className="w-4 h-4" />{streak}
            </span>
          )}
          <span className="text-sm text-green-500 font-medium">{answers.filter(a => a.user_answer !== undefined).length} answered</span>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
      {/* Question with animation feedback */}
      <div className="relative text-center py-8 rounded-2xl transition-all"
        style={{
          animation: feedback?.type === "correct" || feedback?.type === "streak" ? "arena-correct 0.4s ease-out" :
                     feedback?.type === "wrong" ? "arena-wrong 0.4s ease-out" : "none"
        }}>
        <p className="text-5xl sm:text-6xl font-bold font-mono tracking-wide" data-testid="question-display">{q.display} = ?</p>
        {/* Streak fire burst */}
        {feedback?.type === "streak" && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ animation: "arena-streak-fire 0.6s ease-out forwards" }}>
            <span className="text-6xl">&#128293;</span>
          </div>
        )}
        {/* Coin pop */}
        {feedback && (feedback.type === "correct" || feedback.type === "streak") && (
          <div className="absolute top-2 right-4 pointer-events-none" style={{ animation: "arena-coin-pop 0.5s ease-out forwards" }}>
            <span className="text-yellow-500 font-bold text-lg">+1</span>
          </div>
        )}
      </div>
      {/* Answer input */}
      <div className="flex gap-2 max-w-xs mx-auto">
        <input ref={inputRef} type="number" value={userAnswer} onChange={e => setUserAnswer(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") submitAnswer(); }}
          className={`flex-1 h-14 text-2xl font-bold text-center rounded-xl border-2 bg-background focus:outline-none transition-colors ${
            feedback?.type === "correct" || feedback?.type === "streak" ? "border-green-500" :
            feedback?.type === "wrong" ? "border-red-500" : "border-border focus:border-primary"
          }`}
          placeholder="?" autoFocus data-testid="answer-input" />
        <Button className="h-14 w-14 rounded-xl hidden sm:flex" onClick={submitAnswer} data-testid="submit-answer-btn"><ChevronRight className="w-6 h-6" /></Button>
      </div>
      {/* Number Pad for mobile */}
      <NumberPad value={userAnswer} onChange={setUserAnswer} onSubmit={submitAnswer} />
    </div>
  );
}

// ===================== BATTLES =====================

function BattlesTab({ user, classes }) {
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState("");
  const [classStudents, setClassStudents] = useState([]);
  const [selectedOpponent, setSelectedOpponent] = useState("");
  const [activeBattle, setActiveBattle] = useState(null);
  const [battleSession, setBattleSession] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [qStartTime, setQStartTime] = useState(null);
  const [battleAnswers, setBattleAnswers] = useState([]);
  const [battleResults, setBattleResults] = useState(null);
  const [battleFeedback, setBattleFeedback] = useState(null);
  const [battleStreak, setBattleStreak] = useState(0);
  const [classBattles, setClassBattles] = useState([]);
  const [activeClassBattle, setActiveClassBattle] = useState(null);
  const [classBattleQ, setClassBattleQ] = useState(0);
  const [classBattleAnswer, setClassBattleAnswer] = useState("");
  const [classBattleAnswers, setClassBattleAnswers] = useState([]);
  const [classBattleStart, setClassBattleStart] = useState(null);
  const [classBattleResult, setClassBattleResult] = useState(null);
  const [cbFeedback, setCbFeedback] = useState(null);
  const [cbStreak, setCbStreak] = useState(0);
  const [cbTimeLeft, setCbTimeLeft] = useState(0);
  const cbTimerRef = useRef(null);
  const cbAnswersRef = useRef([]);
  const activeCbRef = useRef(null);
  const inputRef = useRef(null);
  const cbInputRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchBattles = useCallback(async () => {
    try {
      const [bRes, ...cbResults] = await Promise.all([
        axios.get(`${API}/arena/battles`, { headers }),
        ...classes.map(c => axios.get(`${API}/arena/class-battle/active/${c.class_id}`, { headers }).catch(() => ({ data: [] })))
      ]);
      setBattles(bRes.data);
      setClassBattles(cbResults.flatMap(r => r.data || []));
    } catch {} finally { setLoading(false); }
  }, [classes]);

  useEffect(() => { fetchBattles(); }, []);

  const joinClassBattle = async (battleId) => {
    try {
      const res = await axios.get(`${API}/arena/class-battle/${battleId}`, { headers });
      if (res.data.already_played) {
        toast.info("You've already played this battle!");
        setClassBattleResult(res.data.my_result);
        return;
      }
      setActiveClassBattle(res.data);
      activeCbRef.current = res.data;
      setClassBattleQ(0);
      setClassBattleAnswers([]);
      cbAnswersRef.current = [];
      setClassBattleStart(Date.now());
      setClassBattleResult(null);
      setCbStreak(0);
      const durSec = Math.max(30, Math.round((res.data.duration_minutes || 3) * 60));
      setCbTimeLeft(durSec);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to join"); }
  };

  const submitClassBattleAnswer = () => {
    if (!classBattleAnswer.trim() || !activeClassBattle) return;
    const timeMs = Date.now() - classBattleStart;
    const q = activeClassBattle.questions[classBattleQ];
    const d = q?.display || "";
    let expected = 0;
    if (d.includes("\u00d7")) { const [a,b] = d.split("\u00d7").map(s => parseInt(s.trim())); expected = a * b; }
    else if (d.includes("\u00f7")) { const [a,b] = d.split("\u00f7").map(s => parseInt(s.trim())); expected = Math.round(a / b); }
    const parsed = parseInt(classBattleAnswer);
    const isCorrect = parsed === expected;
    if (isCorrect) { const ns = cbStreak + 1; setCbStreak(ns); if (ns >= 3 && ns % 3 === 0) { playStreak(); setCbFeedback({ type: "streak" }); } else { playCorrect(); setCbFeedback({ type: "correct" }); } }
    else { setCbStreak(0); playWrong(); setCbFeedback({ type: "wrong" }); }
    setTimeout(() => setCbFeedback(null), 400);

    const newAnswers = [...classBattleAnswers, { index: classBattleQ, user_answer: parsed, time_ms: timeMs }];
    setClassBattleAnswers(newAnswers);
    cbAnswersRef.current = newAnswers;
    setClassBattleAnswer("");
    if (classBattleQ + 1 < activeClassBattle.questions.length) {
      setClassBattleQ(classBattleQ + 1);
      setClassBattleStart(Date.now());
    } else {
      submitClassBattleFinal(newAnswers);
    }
  };

  const submitClassBattleFinal = async (finalAnswers) => {
    if (cbTimerRef.current) { clearInterval(cbTimerRef.current); cbTimerRef.current = null; }
    const battle = activeCbRef.current || activeClassBattle;
    if (!battle) return;
    try {
      const res = await axios.post(`${API}/arena/class-battle/${battle.battle_id}/submit`, { answers: finalAnswers }, { headers });
      setClassBattleResult(res.data);
      setActiveClassBattle(null);
      activeCbRef.current = null;
      setCbTimeLeft(0);
      fetchBattles();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
  };

  // Countdown timer for class battle
  useEffect(() => {
    if (!activeClassBattle || classBattleResult) return;
    cbTimerRef.current = setInterval(() => {
      setCbTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(cbTimerRef.current);
          cbTimerRef.current = null;
          // Auto-submit on timeout with whatever answers we have so far
          toast.info("Time's up! Submitting your answers...");
          submitClassBattleFinal(cbAnswersRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (cbTimerRef.current) { clearInterval(cbTimerRef.current); cbTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClassBattle?.battle_id, classBattleResult]);

  useEffect(() => { if (activeClassBattle && cbInputRef.current) cbInputRef.current.focus(); }, [classBattleQ, activeClassBattle]);

  const fetchStudents = async (classId) => {
    setSelectedClass(classId);
    try {
      const res = await axios.get(`${API}/classes/${classId}/students`, { headers });
      setClassStudents(res.data.filter(s => s.user_id !== user.user_id));
    } catch {}
  };

  const createBattle = async () => {
    if (!selectedOpponent || !selectedClass) return;
    try {
      const res = await axios.post(`${API}/arena/battle/create`, { opponent_id: selectedOpponent, class_id: selectedClass }, { headers });
      toast.success("Battle created!");
      setSelectedOpponent("");
      fetchBattles();
    } catch (e) { toast.error("Failed to create battle"); }
  };

  const openBattle = async (battleId) => {
    try {
      const res = await axios.get(`${API}/arena/battle/${battleId}`, { headers });
      setActiveBattle(res.data);
      if (!res.data.already_played && res.data.questions.length > 0) {
        setBattleSession(res.data);
        setCurrentQ(0);
        setBattleAnswers([]);
        setQStartTime(Date.now());
        setBattleResults(null);
      }
    } catch { toast.error("Failed to load battle"); }
  };

  const getBattleExpectedAnswer = (q) => {
    if (!q) return null;
    const d = q.display;
    if (d.includes("×")) { const [a, b] = d.split("×").map(s => parseInt(s.trim())); return a * b; }
    if (d.includes("÷")) { const [a, b] = d.split("÷").map(s => parseInt(s.trim())); return Math.round(a / b); }
    return null;
  };

  const submitBattleAnswer = () => {
    if (!userAnswer.trim()) return;
    const timeMs = Date.now() - qStartTime;
    const parsed = parseInt(userAnswer);
    const expected = getBattleExpectedAnswer(battleSession.questions[currentQ]);
    const isCorrect = parsed === expected;

    if (isCorrect) {
      const ns = battleStreak + 1;
      setBattleStreak(ns);
      if (ns >= 3 && ns % 3 === 0) { playStreak(); setBattleFeedback({ type: "streak", streak: ns }); }
      else { playCorrect(); setBattleFeedback({ type: "correct", streak: ns }); }
    } else {
      setBattleStreak(0);
      playWrong();
      setBattleFeedback({ type: "wrong", streak: 0 });
    }
    setTimeout(() => setBattleFeedback(null), 500);

    const newAnswers = [...battleAnswers, { index: currentQ, user_answer: parsed, time_ms: timeMs }];
    setBattleAnswers(newAnswers);
    setUserAnswer("");
    if (currentQ + 1 < battleSession.questions.length) {
      setCurrentQ(currentQ + 1);
      setQStartTime(Date.now());
    } else {
      submitBattleFinal(newAnswers);
    }
  };

  const submitBattleFinal = async (finalAnswers) => {
    try {
      const res = await axios.post(`${API}/arena/battle/${activeBattle.battle_id}/submit`, { answers: finalAnswers }, { headers });
      setBattleResults(res.data);
      fetchBattles();
    } catch { toast.error("Failed to submit"); }
  };

  useEffect(() => { if (battleSession && inputRef.current) inputRef.current.focus(); }, [currentQ, battleSession]);

  // Playing a battle
  if (battleSession && !battleResults && !activeBattle?.already_played) {
    const q = battleSession.questions[currentQ];
    return (
      <div className="space-y-4" data-testid="battle-gameplay">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Battle: Q {currentQ + 1}/{battleSession.questions.length}</span>
          <div className="flex items-center gap-2">
            {battleStreak >= 3 && <span className="flex items-center gap-1 text-orange-500 font-bold text-sm"><Flame className="w-4 h-4" />{battleStreak}</span>}
            <span className="text-sm text-muted-foreground">vs {activeBattle.is_creator ? activeBattle.opponent_name : activeBattle.creator_name}</span>
          </div>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${(currentQ / battleSession.questions.length) * 100}%` }} /></div>
        <div className="relative text-center py-8 rounded-2xl" style={{
          animation: battleFeedback?.type === "correct" || battleFeedback?.type === "streak" ? "arena-correct 0.4s ease-out" :
                     battleFeedback?.type === "wrong" ? "arena-wrong 0.4s ease-out" : "none"
        }}>
          <p className="text-5xl font-bold font-mono">{q.display} = ?</p>
          {battleFeedback?.type === "streak" && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ animation: "arena-streak-fire 0.6s ease-out forwards" }}><span className="text-6xl">&#128293;</span></div>}
        </div>
        <div className="flex gap-2 max-w-xs mx-auto">
          <input ref={inputRef} type="number" value={userAnswer} onChange={e => setUserAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitBattleAnswer(); }}
            className={`flex-1 h-14 text-2xl font-bold text-center rounded-xl border-2 bg-background focus:outline-none transition-colors ${
              battleFeedback?.type === "correct" || battleFeedback?.type === "streak" ? "border-green-500" :
              battleFeedback?.type === "wrong" ? "border-red-500" : "border-border focus:border-primary"
            }`} placeholder="?" autoFocus data-testid="battle-answer-input" />
          <Button className="h-14 w-14 rounded-xl hidden sm:flex" onClick={submitBattleAnswer}><ChevronRight className="w-6 h-6" /></Button>
        </div>
        <NumberPad value={userAnswer} onChange={setUserAnswer} onSubmit={submitBattleAnswer} />
      </div>
    );
  }

  // Battle result / detail
  if (activeBattle) {
    return (
      <div className="space-y-4" data-testid="battle-detail">
        <Button variant="ghost" size="sm" onClick={() => { setActiveBattle(null); setBattleSession(null); setBattleResults(null); }}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Card><CardContent className="p-6 text-center space-y-3">
          <h3 className="text-xl font-bold">{activeBattle.creator_name} vs {activeBattle.opponent_name}</h3>
          <p className="text-sm text-muted-foreground">Status: {activeBattle.status === "completed" ? "Completed" : activeBattle.already_played ? "Waiting for opponent..." : "Your turn!"}</p>
          {battleResults && <p className="text-green-500 font-bold">You scored {battleResults.result.correct}/{battleResults.result.total} (+{battleResults.coins_earned} coins)</p>}
          {activeBattle.status === "completed" && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className={`p-3 rounded-xl ${activeBattle.winner_id === activeBattle.creator_name ? "bg-green-500/10 ring-2 ring-green-500" : "bg-muted"}`}>
                <p className="font-bold">{activeBattle.creator_name}</p>
                <p className="text-2xl font-bold">{activeBattle.creator_result?.correct || 0}</p>
                <p className="text-xs text-muted-foreground">{activeBattle.creator_result?.avg_time_ms ? `${(activeBattle.creator_result.avg_time_ms/1000).toFixed(1)}s avg` : ""}</p>
              </div>
              <div className={`p-3 rounded-xl ${activeBattle.winner_id === activeBattle.opponent_name ? "bg-green-500/10 ring-2 ring-green-500" : "bg-muted"}`}>
                <p className="font-bold">{activeBattle.opponent_name}</p>
                <p className="text-2xl font-bold">{activeBattle.opponent_result?.correct || 0}</p>
                <p className="text-xs text-muted-foreground">{activeBattle.opponent_result?.avg_time_ms ? `${(activeBattle.opponent_result.avg_time_ms/1000).toFixed(1)}s avg` : ""}</p>
              </div>
            </div>
          )}
        </CardContent></Card>
      </div>
    );
  }

  // Playing a class battle
  if (activeClassBattle && !classBattleResult) {
    const q = activeClassBattle.questions[classBattleQ];
    const progress = (classBattleQ / activeClassBattle.questions.length) * 100;
    return (
      <div className="space-y-4" data-testid="class-battle-gameplay">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium truncate">{activeClassBattle.title}: Q {classBattleQ + 1}/{activeClassBattle.questions.length}</span>
          <div className="flex items-center gap-3 shrink-0">
            {cbStreak >= 3 && <span className="flex items-center gap-1 text-orange-500 font-bold text-sm"><Flame className="w-4 h-4" />{cbStreak}</span>}
            <span className={`flex items-center gap-1 text-sm font-bold font-mono ${cbTimeLeft <= 10 ? "text-red-500 animate-pulse" : cbTimeLeft <= 30 ? "text-orange-500" : "text-foreground"}`} data-testid="cb-timer">
              <Timer className="w-4 h-4" />{Math.floor(cbTimeLeft / 60)}:{String(cbTimeLeft % 60).padStart(2, "0")}
            </span>
          </div>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${progress}%` }} /></div>
        <div className="relative text-center py-6 rounded-2xl" style={{
          animation: cbFeedback?.type === "correct" || cbFeedback?.type === "streak" ? "arena-correct 0.4s ease-out" : cbFeedback?.type === "wrong" ? "arena-wrong 0.4s ease-out" : "none"
        }}>
          <p className="text-4xl sm:text-5xl font-bold font-mono">{q?.display} = ?</p>
          {cbFeedback?.type === "streak" && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ animation: "arena-streak-fire 0.6s ease-out forwards" }}><span className="text-5xl">&#128293;</span></div>}
        </div>
        <div className="flex gap-2 max-w-xs mx-auto">
          <input ref={cbInputRef} type="number" value={classBattleAnswer} onChange={e => setClassBattleAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submitClassBattleAnswer(); }}
            className={`flex-1 h-14 text-2xl font-bold text-center rounded-xl border-2 bg-background focus:outline-none transition-colors ${
              cbFeedback?.type === "correct" || cbFeedback?.type === "streak" ? "border-green-500" : cbFeedback?.type === "wrong" ? "border-red-500" : "border-border focus:border-primary"
            }`} placeholder="?" autoFocus data-testid="cb-answer-input" />
          <Button className="h-14 w-14 rounded-xl hidden sm:flex" onClick={submitClassBattleAnswer}><ChevronRight className="w-6 h-6" /></Button>
        </div>
        <NumberPad value={classBattleAnswer} onChange={setClassBattleAnswer} onSubmit={submitClassBattleAnswer} />
      </div>
    );
  }

  // Class battle result
  if (classBattleResult && !activeClassBattle) {
    const r = classBattleResult.result || classBattleResult;
    return (
      <div className="space-y-4 text-center" data-testid="class-battle-result">
        <h2 className="text-2xl font-bold">Class Battle Complete!</h2>
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-500">{r.score}</p><p className="text-xs text-muted-foreground">Correct</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{r.answered}</p><p className="text-xs text-muted-foreground">Answered</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-yellow-500">+{classBattleResult.coins_earned || r.score}</p><p className="text-xs text-muted-foreground">Coins</p></CardContent></Card>
        </div>
        <Button onClick={() => setClassBattleResult(null)} className="w-full"><ArrowLeft className="w-4 h-4 mr-2" />Back to Battles</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="battles-tab">
      <h2 className="text-2xl font-bold font-['Manrope']">Battles</h2>

      {/* Active Class Battles from Teacher */}
      {classBattles.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-purple-500 flex items-center gap-1"><Swords className="w-4 h-4" />Class Battles</h3>
          {classBattles.map(b => (
            <Card key={b.battle_id} className="cursor-pointer border-purple-500/20 bg-purple-500/5 hover:border-purple-500/40 transition-all" onClick={() => joinClassBattle(b.battle_id)} data-testid={`class-battle-${b.battle_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-bold text-sm">{b.title}</p>
                  <p className="text-xs text-muted-foreground">{b.class_name} &middot; {b.duration_minutes} min &middot; by {b.teacher_name}</p>
                </div>
                <span className="px-3 py-1 text-xs rounded-full bg-purple-500 text-white font-medium">Join</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create 1v1 battle */}
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Challenge a Classmate</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Select value={selectedClass} onValueChange={fetchStudents}><SelectTrigger data-testid="battle-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}</SelectContent></Select>
          {classStudents.length > 0 && (
            <Select value={selectedOpponent} onValueChange={setSelectedOpponent}><SelectTrigger data-testid="battle-opponent-select"><SelectValue placeholder="Select opponent" /></SelectTrigger>
              <SelectContent>{classStudents.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent></Select>
          )}
          <Button onClick={createBattle} disabled={!selectedOpponent} className="w-full" data-testid="create-battle-btn"><Swords className="w-4 h-4 mr-2" />Challenge!</Button>
        </CardContent>
      </Card>
      {/* Battle list */}
      <div className="space-y-2">
        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> :
          battles.length === 0 ? <p className="text-center text-muted-foreground py-8">No battles yet. Challenge someone!</p> :
          battles.map(b => {
            const isCreator = b.creator_id === user.user_id;
            const myResult = isCreator ? b.creator_result : b.opponent_result;
            return (
              <Card key={b.battle_id} className="cursor-pointer hover:border-primary/30 transition-all" onClick={() => openBattle(b.battle_id)} data-testid={`battle-${b.battle_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div><p className="font-bold text-sm">{b.creator_name} vs {b.opponent_name}</p><p className="text-xs text-muted-foreground">{b.status === "completed" ? "Completed" : myResult ? "Waiting..." : "Your turn!"}</p></div>
                  <div className="flex items-center gap-2">
                    {b.status === "completed" ? <Check className="w-4 h-4 text-green-500" /> : !myResult ? <span className="px-2 py-0.5 text-xs rounded-full bg-orange-500/10 text-orange-500 font-medium">Play</span> : <Timer className="w-4 h-4 text-muted-foreground" />}
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>
    </div>
  );
}

// ===================== LEADERBOARD =====================

function LeaderboardTab({ user, classes }) {
  const [view, setView] = useState("global");
  const [selectedClass, setSelectedClass] = useState(classes[0]?.class_id || "");
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const url = view === "global" ? `${API}/arena/leaderboard/global` :
        view === "weekly" ? `${API}/arena/leaderboard/weekly/${selectedClass}` :
        `${API}/arena/leaderboard/class/${selectedClass}`;
      const res = await axios.get(url, { headers });
      setLeaderboard(res.data.leaderboard || []);
    } catch {} finally { setLoading(false); }
  }, [view, selectedClass]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  return (
    <div className="space-y-4" data-testid="leaderboard-tab">
      <h2 className="text-2xl font-bold font-['Manrope']">Arena Leaderboard</h2>
      <div className="flex gap-2 flex-wrap">
        {["global", "class", "weekly"].map(v => (
          <Button key={v} variant={view === v ? "default" : "outline"} size="sm" onClick={() => setView(v)} data-testid={`lb-${v}-btn`}>
            {v === "global" ? "Global" : v === "class" ? "Class" : "Weekly"}
          </Button>
        ))}
      </div>
      {view !== "global" && (
        <Select value={selectedClass} onValueChange={setSelectedClass}><SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}</SelectContent></Select>
      )}
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> :
        leaderboard.length === 0 ? <p className="text-center text-muted-foreground py-8">No data yet. Start playing!</p> :
        <div className="space-y-2">
          {leaderboard.map((entry, i) => {
            const isMe = entry.user_id === user.user_id;
            const speed = view === "weekly" ? entry.avg_speed_ms : entry.avg_speed_ms;
            const score = view === "weekly" ? entry.weekly_correct : entry.total_correct;
            return (
              <div key={entry.user_id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isMe ? "bg-primary/5 ring-1 ring-primary/20" : "bg-muted/30"}`} data-testid={`lb-entry-${i}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-yellow-500 text-white" : i === 1 ? "bg-gray-300 text-gray-700" : i === 2 ? "bg-orange-400 text-white" : "bg-muted text-muted-foreground"}`}>{i + 1}</span>
                <AvatarDisplay avatar={entry.avatar} size="w-10 h-10" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{entry.name}{isMe ? " (You)" : ""}</p>
                  <p className="text-xs text-muted-foreground">{entry.rank || "Beginner"} {entry.streak_days > 1 ? `• ${entry.streak_days}d streak` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">{score}</p>
                  <p className="text-xs text-muted-foreground">{speed ? `${(speed / 1000).toFixed(1)}s` : "-"}</p>
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}

// ===================== STATS =====================

function StatsTab({ user }) {
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  useEffect(() => {
    (async () => {
      try {
        const [sRes, pRes] = await Promise.all([
          axios.get(`${API}/arena/stats`, { headers }),
          axios.get(`${API}/arena/profile`, { headers })
        ]);
        setStats(sRes.data);
        setProfile(pRes.data);
      } catch {}
    })();
  }, []);

  if (!stats || !profile) return <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8" />;

  const heatmap = stats.heatmap || {};
  const tables = [2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20];
  const multipliers = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20];

  return (
    <div className="space-y-6" data-testid="stats-tab">
      <h2 className="text-2xl font-bold font-['Manrope']">My Stats</h2>
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{profile.total_sessions}</p><p className="text-xs text-muted-foreground">Sessions</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-500">{profile.total_correct}</p><p className="text-xs text-muted-foreground">Correct</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{profile.avg_speed_ms ? `${(profile.avg_speed_ms/1000).toFixed(1)}s` : "-"}</p><p className="text-xs text-muted-foreground">Avg Speed</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-yellow-500">{profile.coins}</p><p className="text-xs text-muted-foreground">Coins</p></CardContent></Card>
      </div>
      {/* Heatmap */}
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Fact Mastery Heatmap</CardTitle><CardDescription>Green = strong, Red = needs practice</CardDescription></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="heatmap-table">
              <thead><tr><th className="p-1"></th>{multipliers.map(m => <th key={m} className="p-1 text-center font-bold">{m}</th>)}</tr></thead>
              <tbody>{tables.map(t => (
                <tr key={t}><td className="p-1 font-bold text-center">{t}x</td>
                  {multipliers.map(m => {
                    const key = `${t}x${m}`;
                    const d = heatmap[key];
                    const bg = !d ? "bg-muted/50" : d.accuracy >= 0.8 ? "bg-green-500/70 text-white" : d.accuracy >= 0.5 ? "bg-yellow-500/70" : "bg-red-500/70 text-white";
                    return <td key={m} className={`p-1 text-center rounded ${bg}`} title={d ? `${Math.round(d.accuracy * 100)}% (${d.asked} tries)` : "Not practiced"}>{d ? `${Math.round(d.accuracy * 100)}%` : "-"}</td>;
                  })}
                </tr>
              ))}</tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===================== SHOP =====================

function ShopTab({ user, profile, onUpdate }) {
  const [localAvatar, setLocalAvatar] = useState(profile?.avatar || {});
  const [ownedItems, setOwnedItems] = useState(profile?.owned_items || []);
  const [category, setCategory] = useState("skin");
  const [saving, setSaving] = useState(false);
  const [coins, setCoins] = useState(profile?.coins || 0);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  useEffect(() => {
    setCoins(profile?.coins || 0);
    setOwnedItems(profile?.owned_items || []);
  }, [profile]);

  const isOwned = (itemId) => {
    const item = findItem(itemId);
    return item?.cost === 0 || ownedItems.includes(itemId) || localAvatar[category] === itemId;
  };

  const findItem = (itemId) => {
    for (const cat of Object.values(AVATAR_CATALOG)) {
      const found = cat.find(i => i.id === itemId);
      if (found) return found;
    }
    return null;
  };

  const selectItem = async (itemId) => {
    const item = findItem(itemId);
    if (!item) return;
    const isFree = item.cost === 0 || ownedItems.includes(itemId);
    const cost = isFree ? 0 : item.cost;

    if (cost > 0 && coins < cost) {
      toast.error(`Not enough coins! Need ${cost}, have ${coins}`);
      return;
    }

    const newAvatar = { ...localAvatar, [category]: itemId };
    // Preserve color overrides for hair/outfit
    if (category === "hair" && item.color) newAvatar.hairColor = item.color;
    if (category === "outfit" && item.color) newAvatar.outfitColor = item.color;

    setSaving(true);
    try {
      await axios.put(`${API}/arena/profile/avatar`, { avatar: newAvatar, cost }, { headers });
      setLocalAvatar(newAvatar);
      if (cost > 0) {
        setCoins(c => c - cost);
        setOwnedItems(prev => [...prev, itemId]);
        toast.success(`Purchased & equipped! (-${cost} coins)`);
      } else {
        toast.success("Equipped!");
      }
      if (onUpdate) onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const items = AVATAR_CATALOG[category] || [];

  return (
    <div className="space-y-5" data-testid="shop-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold font-['Manrope']">Avatar Studio</h2>
        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500/10 text-yellow-500 font-bold text-sm"><Star className="w-4 h-4" />{coins} coins</span>
      </div>

      {/* Preview + Stats */}
      <div className="flex flex-col items-center gap-3">
        <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-xl ring-2 ring-border">
          <AvatarRenderer avatar={localAvatar} size={128} />
        </div>
        <div className="text-center">
          <p className="font-medium text-sm">{profile?.name || user?.name}</p>
          <p className="text-xs text-muted-foreground">{ownedItems.length} items owned</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
        {CATEGORY_ORDER.map(cat => (
          <button key={cat} onClick={() => setCategory(cat)} data-testid={`cat-${cat}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${category === cat ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="shop-items-grid">
        {items.map(item => {
          const equipped = localAvatar[category] === item.id;
          const owned = isOwned(item.id);
          return (
            <button key={item.id} onClick={() => selectItem(item.id)} disabled={saving || equipped}
              data-testid={`item-${item.id}`}
              className={`relative p-2 rounded-xl border-2 transition-all text-center ${equipped ? "border-primary bg-primary/5 shadow-md" : owned ? "border-green-500/30 bg-green-500/5 hover:border-green-500/50" : "border-border hover:border-primary/30 hover:shadow-sm"}`}>
              {/* Mini preview */}
              <div className="w-full aspect-square rounded-lg overflow-hidden bg-slate-100 mb-1.5 flex items-center justify-center">
                {category === "background" ? (
                  <div className="w-full h-full rounded-lg" style={{
                    background: item.gradient ? `linear-gradient(135deg, ${item.gradient.join(",")})` : item.color
                  }} />
                ) : category === "skin" ? (
                  <div className="w-10 h-10 rounded-full" style={{ background: item.color }} />
                ) : (
                  <svg width="60" height="60" viewBox={
                    category === "hair" ? "25 5 60 40" :
                    category === "eyes" ? "30 35 50 25" :
                    category === "mouth" ? "35 50 40 25" :
                    category === "outfit" ? "20 70 70 45" :
                    category === "accessory" ? "20 15 80 80" : "0 0 110 110"
                  }>
                    {category === "hair" && item.render?.(item.color)}
                    {category === "eyes" && item.render?.()}
                    {category === "mouth" && item.render?.()}
                    {category === "outfit" && item.render?.(item.color)}
                    {category === "accessory" && item.render?.()}
                  </svg>
                )}
              </div>
              <p className="text-xs font-medium truncate">{item.label}</p>
              {/* Badge */}
              {equipped ? (
                <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center"><Check className="w-3 h-3 text-white" /></span>
              ) : owned || item.cost === 0 ? (
                <p className="text-[10px] text-green-500">Free</p>
              ) : (
                <p className="text-[10px] text-yellow-500 font-medium">{item.cost} coins</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ===================== MAIN PAGE =====================


// ===================== BATTLE LEADERBOARD =====================

function BattleLeaderboardTab({ user, classes }) {
  const [view, setView] = useState("global");
  const [selectedClass, setSelectedClass] = useState(classes[0]?.class_id || "");
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const url = view === "global" ? `${API}/arena/battle-leaderboard/global` :
        view === "weekly" ? `${API}/arena/battle-leaderboard/weekly/${selectedClass}` :
        `${API}/arena/battle-leaderboard/class/${selectedClass}`;
      const res = await axios.get(url, { headers });
      setLeaderboard(res.data.leaderboard || []);
    } catch {} finally { setLoading(false); }
  }, [view, selectedClass]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  return (
    <div className="space-y-4" data-testid="battle-leaderboard-tab">
      <h2 className="text-2xl font-bold font-['Manrope']">Battle Leaderboard</h2>
      <p className="text-sm text-muted-foreground">Rankings from teacher-assigned class battles only</p>
      <div className="flex gap-2 flex-wrap">
        {["global", "class", "weekly"].map(v => (
          <Button key={v} variant={view === v ? "default" : "outline"} size="sm" onClick={() => setView(v)} data-testid={`blb-${v}-btn`}>
            {v === "global" ? "Global" : v === "class" ? "Class" : "Weekly"}
          </Button>
        ))}
      </div>
      {view !== "global" && (
        <Select value={selectedClass} onValueChange={setSelectedClass}><SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}</SelectContent></Select>
      )}
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> :
        leaderboard.length === 0 ? <p className="text-center text-muted-foreground py-8">No battle data yet. Join a class battle to appear here!</p> :
        <div className="space-y-2">
          {leaderboard.map((entry, i) => {
            const isMe = entry.user_id === user.user_id;
            const battles = view === "weekly" ? entry.weekly_battles : entry.total_battles;
            const score = view === "weekly" ? entry.weekly_score : entry.total_score;
            return (
              <div key={entry.user_id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isMe ? "bg-purple-500/5 ring-1 ring-purple-500/20" : "bg-muted/30"}`} data-testid={`blb-entry-${i}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-yellow-500 text-white" : i === 1 ? "bg-gray-300 text-gray-700" : i === 2 ? "bg-orange-400 text-white" : "bg-muted text-muted-foreground"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{entry.name}{isMe ? " (You)" : ""}</p>
                  <p className="text-xs text-muted-foreground">{battles} battle{battles !== 1 ? "s" : ""} · avg {entry.avg_score}/battle{entry.avg_time_ms ? ` · ${(entry.avg_time_ms / 1000).toFixed(1)}s avg` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm text-purple-500">{score}</p>
                  <p className="text-xs text-muted-foreground">total</p>
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}


export default function ArenaPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [classes, setClasses] = useState([]);
  const [tab, setTab] = useState("play");
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/arena/profile`, { headers });
      setProfile(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    fetchProfile();
    (async () => {
      try {
        const res = await axios.get(`${API}/classes`, { headers });
        setClasses(res.data);
      } catch {}
    })();
  }, [user]);

  if (!user) return null;

  const RankIcon = RANK_ICONS[profile?.rank] || Shield;
  const rankGradient = RANK_COLORS[profile?.rank] || "from-gray-400 to-gray-500";

  return (
    <div className="min-h-screen bg-background" data-testid="arena-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Manrope']">Times Tables Arena</h1>
              <p className="text-sm text-white/70">Master your multiplication & division</p>
            </div>
          </div>
          {profile && (
            <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-4">
              <AvatarDisplay avatar={profile.avatar} size="w-14 h-14" />
              <div className="flex-1">
                <p className="font-bold">{profile.name || user.name}</p>
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r ${rankGradient} text-xs font-bold`}>
                  <RankIcon className="w-3 h-3" />{profile.rank || "Beginner"}
                </div>
              </div>
              <div className="text-right text-sm">
                <p className="font-bold text-yellow-300"><Star className="w-3 h-3 inline" /> {profile.coins} coins</p>
                <p className="text-white/70">{profile.streak_days > 0 ? `${profile.streak_days}d streak` : "No streak"}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-6 mb-6 h-auto">
            <TabsTrigger value="play" className="text-xs sm:text-sm py-2" data-testid="tab-play"><Zap className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Play</span></TabsTrigger>
            <TabsTrigger value="battles" className="text-xs sm:text-sm py-2" data-testid="tab-battles"><Swords className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Battles</span></TabsTrigger>
            <TabsTrigger value="leaderboard" className="text-xs sm:text-sm py-2" data-testid="tab-leaderboard"><Trophy className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Arena</span></TabsTrigger>
            <TabsTrigger value="battle-ranks" className="text-xs sm:text-sm py-2" data-testid="tab-battle-ranks"><Crown className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Battle</span></TabsTrigger>
            <TabsTrigger value="stats" className="text-xs sm:text-sm py-2" data-testid="tab-stats"><BarChart3 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Stats</span></TabsTrigger>
            <TabsTrigger value="shop" className="text-xs sm:text-sm py-2" data-testid="tab-shop"><ShoppingBag className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Shop</span></TabsTrigger>
          </TabsList>
          <TabsContent value="play"><PlayMode user={user} profile={profile} classes={classes} onComplete={fetchProfile} /></TabsContent>
          <TabsContent value="battles"><BattlesTab user={user} classes={classes} /></TabsContent>
          <TabsContent value="leaderboard"><LeaderboardTab user={user} classes={classes} /></TabsContent>
          <TabsContent value="battle-ranks"><BattleLeaderboardTab user={user} classes={classes} /></TabsContent>
          <TabsContent value="stats"><StatsTab user={user} /></TabsContent>
          <TabsContent value="shop"><ShopTab user={user} profile={profile} onUpdate={fetchProfile} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
